import { useState, type ComponentProps } from 'react';
import { ActivityIndicator, Pressable, ScrollView, Text, View } from 'react-native';
import { useIsFocused, useRouter, type Href } from 'expo-router';
import type { ForkShape } from '@perdiem/trpc/fire-forks-types';
import { hasRunnableForkOverrides } from '@perdiem/trpc/fire-forks-overrides';
import { Ionicons } from '@expo/vector-icons';
import { trpc } from '../../lib/trpc';
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  FormErrorBanner,
  LoadingSpinner,
  PageContainer,
  PageHeader,
  SectionHeader,
  WebHead,
} from '../../components';
import { useColors } from '../../lib/colors';
import { formatFiAgeSummary } from '../../lib/fire-summary';
import { SCENARIO_INTENTS, type ScenarioIntentOption } from '../../lib/scenario-intents';
import { summarizeScenarioOverrides } from '@perdiem/trpc/fire-forks-summary';

type IoniconName = ComponentProps<typeof Ionicons>['name'];

interface FiProfileContext {
  primaryName: string | null;
  primaryBirthYear: number | null;
  spouseName: string | null;
  spouseBirthYear: number | null;
}

function describeFi(fiYear: number | null, ctx: FiProfileContext): string {
  const primaryFiAge =
    ctx.primaryBirthYear && fiYear ? fiYear - ctx.primaryBirthYear : null;
  const spouseFiAge =
    ctx.spouseBirthYear && fiYear ? fiYear - ctx.spouseBirthYear : null;
  return formatFiAgeSummary({
    fiYear,
    primaryName: ctx.primaryName,
    primaryFiAge,
    spouseName: ctx.spouseName,
    spouseFiAge,
  });
}

function computeFiDelta(fork: ForkShape, defaultBeginYear: number | null): {
  label: string;
  variant: 'green' | 'red' | 'gray';
} | null {
  if (!fork || fork.isStale || !fork.lastResult) return null;
  const forkYear = fork.lastResult.beginYear;
  if (forkYear == null || defaultBeginYear == null) return null;
  const delta = forkYear - defaultBeginYear;
  if (delta === 0) return { label: 'Same as default', variant: 'gray' };
  const n = Math.abs(delta);
  const noun = n === 1 ? 'year' : 'years';
  const tone = delta < 0 ? 'earlier' : 'later';
  return {
    label: `${n} ${noun} ${tone}`,
    variant: delta < 0 ? 'green' : 'red',
  };
}

function isForkCalculationRunning(fork: ForkShape): boolean {
  const status = fork.calculationStatus?.status;
  return (
    status === 'starting' ||
    status === 'precalculating' ||
    status === 'simulating' ||
    status === 'saving'
  );
}

function getForkCalculationError(fork: ForkShape): string | null {
  return fork.calculationStatus?.status === 'error'
    ? (fork.calculationStatus.error ?? 'Calculation failed')
    : null;
}

export default function ScenariosScreen() {
  const router = useRouter();
  const isFocused = useIsFocused();
  const utils = trpc.useUtils();
  const c = useColors();

  const forksQ = trpc.fire.forks.list.useQuery(undefined, {
    enabled: isFocused,
    refetchInterval: (query) => {
      const hasRunningFork = query.state.data?.some(isForkCalculationRunning);
      return isFocused && hasRunningFork ? 3000 : false;
    },
  });
  const householdQ = trpc.households.get.useQuery(undefined, { enabled: isFocused });
  const planQ = trpc.fire.getPlan.useQuery(undefined, { enabled: isFocused });
  const profilesQ = trpc.profiles.list.useQuery(undefined, { enabled: isFocused });
  const incomeQ = trpc.income.list.useQuery(undefined, { enabled: isFocused });
  const holdingsQ = trpc.holdings.list.useQuery(undefined, { enabled: isFocused });
  const eventsQ = trpc.fire.listEvents.useQuery(undefined, { enabled: isFocused });

  const [pendingCalculateForkId, setPendingCalculateForkId] = useState<string | null>(null);
  const calculateForkMutation = trpc.fire.forks.calculate.useMutation({
    onSettled: () => {
      setPendingCalculateForkId(null);
      void utils.fire.forks.list.invalidate();
    },
  });
  const forks = forksQ.data ?? [];
  const userForks = forks.filter((f) => f.kind === 'user');
  const isDemo = householdQ.data?.isDemo === true;
  const canMutateScenarios = householdQ.data != null && !isDemo;
  const runningForkId = userForks.find(isForkCalculationRunning)?.id ?? null;
  const activeCalculateForkId = pendingCalculateForkId ?? runningForkId;

  function recalculateFork(fork: ForkShape) {
    if (!canMutateScenarios || activeCalculateForkId) return;
    setPendingCalculateForkId(fork.id);
    calculateForkMutation.mutate({ id: fork.id });
  }

  if (forksQ.isLoading || planQ.isLoading) {
    return (
      <ScrollView
        className="flex-1 bg-canvas dark:bg-canvas-dark"
        contentContainerStyle={{ flexGrow: 1 }}
      >
        <PageContainer className="p-4" size="default">
          <LoadingSpinner />
        </PageContainer>
      </ScrollView>
    );
  }

  if (forksQ.error) {
    return (
      <ErrorState
        message={`Couldn't load scenarios: ${forksQ.error.message}`}
        onRetry={() => forksQ.refetch()}
      />
    );
  }

  const defaultBeginYear = planQ.data?.beginYear ?? null;
  const profiles = profilesQ.data ?? [];
  const primaryProfile = profiles.find((p) => p.type === 'primary');
  const spouseProfile = profiles.find((p) => p.type === 'spouse');
  const fiCtx: FiProfileContext = {
    primaryName: primaryProfile?.firstName ?? null,
    primaryBirthYear: primaryProfile?.birthYear ?? null,
    spouseName: spouseProfile?.firstName ?? null,
    spouseBirthYear: spouseProfile?.birthYear ?? null,
  };
  const wageStreamNames = new Map(
    (incomeQ.data ?? []).map((s) => [s.id, s.name]),
  );
  const holdingLabels = new Map(
    (holdingsQ.data?.items ?? []).map((h) => [
      h.id,
      h.symbol ?? 'Largest holding',
    ]),
  );
  const lifeEventNames = new Map(
    (eventsQ.data ?? []).map((event) => [event.id, event.description]),
  );
  return (
    <>
      <WebHead><title>Scenarios — Per Diem</title></WebHead>
      <ScrollView
        className="flex-1 bg-canvas dark:bg-canvas-dark"
        contentContainerStyle={{ flexGrow: 1, paddingBottom: 24 }}
      >
        <PageContainer className="p-4" size="default">
          <PageHeader
            title="Scenarios"
            subtitle="What-if scenarios for your FIRE plan"
            closeTo="/(app)/fire-plan"
          />

          <View className="mb-4">
            <Card className="border border-green-200 bg-forest-50 dark:border-gray-700 dark:bg-gray-800">
              <Text className="text-xs font-semibold uppercase text-forest-700 dark:text-green-400">
                Default plan
              </Text>
              <Text className="text-lg font-bold text-gray-900 dark:text-gray-100 mt-1">
                {defaultBeginYear != null
                  ? describeFi(defaultBeginYear, fiCtx)
                  : 'Run calculate to see your FI year'}
              </Text>
            </Card>
          </View>

          {canMutateScenarios && (
            <ScenarioIntentPicker
              onSelect={(intent) =>
                router.push(`/(app)/scenarios/new?intent=${intent.id}` as Href)
              }
            />
          )}

          <FormErrorBanner
            error={calculateForkMutation.error?.message ?? null}
          />

          <SectionHeader
            title="My Scenarios"
            action={canMutateScenarios ? (
              <Pressable
                onPress={() => router.push('/(app)/scenarios/new' as Href)}
                className="p-1"
                accessibilityRole="button"
                accessibilityLabel="New scenario"
              >
                <Ionicons name="add" size={22} color={c.primary} />
              </Pressable>
            ) : undefined}
          />
          {isDemo && (
            <Card className="mb-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <Text className="text-sm font-semibold text-amber-900 dark:text-amber-300">
                Read-only demo account
              </Text>
              <Text className="text-xs text-amber-800 dark:text-amber-200 mt-1">
                Scenario creation and editing are disabled in demos.
              </Text>
            </Card>
          )}
          {userForks.length === 0 ? (
            <EmptyState
              icon={
                <View className="h-14 w-14 items-center justify-center rounded-2xl border border-green-200 bg-forest-50 dark:border-gray-700 dark:bg-gray-800">
                  <Ionicons name="git-branch-outline" size={26} color={c.primary} />
                </View>
              }
              title="No scenarios yet"
              message={
                isDemo
                  ? 'This demo profile does not have saved scenarios yet.'
                  : 'Create a scenario to model a what-if (different state, higher income, etc.) without touching your default plan.'
              }
              {...(canMutateScenarios
                ? {
                    actionLabel: '+ New scenario',
                    onAction: () => router.push('/(app)/scenarios/new' as Href),
                  }
                : {})}
            />
          ) : (
            <View>
              {userForks.map((fork) => (
                <ForkRowCard
                  key={fork.id}
                  fork={fork}
                  fiCtx={fiCtx}
                  bullets={summarizeScenarioOverrides(fork.overrides, {
                    wageStreamNames,
                    holdingLabels,
                    lifeEventNames,
                  })}
                  delta={computeFiDelta(fork, defaultBeginYear)}
                  isRecalculating={
                    isForkCalculationRunning(fork) ||
                    pendingCalculateForkId === fork.id
                  }
                  canRecalculate={
                    canMutateScenarios &&
                    fork.isStale &&
                    hasRunnableForkOverrides(fork.overrides)
                  }
                  recalculateDisabled={
                    activeCalculateForkId != null &&
                    activeCalculateForkId !== fork.id
                  }
                  calculationError={getForkCalculationError(fork)}
                  onPress={() => router.push(`/(app)/scenarios/${fork.id}` as Href)}
                  onRecalculate={() => recalculateFork(fork)}
                />
              ))}
            </View>
          )}
        </PageContainer>
      </ScrollView>

    </>
  );
}

interface ForkRowCardProps {
  fork: ForkShape;
  fiCtx: FiProfileContext;
  bullets: string[];
  delta: ReturnType<typeof computeFiDelta>;
  isRecalculating: boolean;
  canRecalculate: boolean;
  recalculateDisabled: boolean;
  calculationError: string | null;
  onPress: () => void;
  onRecalculate: () => void;
}

function ScenarioIntentPicker({
  onSelect,
}: {
  onSelect: (intent: ScenarioIntentOption) => void;
}) {
  return (
    <View className="mb-5">
      <Text className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 mb-2">
        What would you like to simulate?
      </Text>
      <View className="flex-row flex-wrap gap-2">
        {SCENARIO_INTENTS.map((intent) => (
          <Pressable
            key={intent.id}
            onPress={() => onSelect(intent)}
            className="w-full sm:flex-1 sm:min-w-[220px] rounded-xl border border-gray-200 bg-white px-4 py-3 active:opacity-70 dark:border-gray-700 dark:bg-gray-800"
            accessibilityRole="button"
            accessibilityLabel={`Create scenario for ${intent.label}`}
          >
            <View className="flex-row items-start gap-3">
              <View className="mt-0.5 h-8 w-8 items-center justify-center rounded-full bg-forest-50 dark:bg-forest-900/40">
                <Ionicons
                  name={intent.iconName as IoniconName}
                  size={17}
                  color="#2B7A50"
                />
              </View>
              <View className="flex-1">
                <Text className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                  {intent.label}
                </Text>
                <Text className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {intent.detail}
                </Text>
              </View>
            </View>
          </Pressable>
        ))}
      </View>
    </View>
  );
}

function ForkRowCard({
  fork,
  fiCtx,
  bullets,
  delta,
  isRecalculating,
  canRecalculate,
  recalculateDisabled,
  calculationError,
  onPress,
  onRecalculate,
}: ForkRowCardProps) {
  const c = useColors();

  return (
    <View className="mb-3">
      <Card>
        <Pressable
          onPress={onPress}
          className="flex-row items-center"
          accessibilityRole="button"
          accessibilityLabel={`Open ${fork.name}`}
        >
          <View className="flex-1 pr-3">
            <Text className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              {fork.name}
            </Text>
            {bullets.length > 0 && (
              <View className="mt-2">
                {bullets.map((b) => (
                  <Text
                    key={b}
                    className="text-xs text-gray-600 dark:text-gray-400"
                  >
                    {`• ${b}`}
                  </Text>
                ))}
              </View>
            )}
            <View className="flex-row items-center gap-3 mt-2 flex-wrap">
              {isRecalculating ? (
                <View className="flex-row items-center gap-1.5">
                  <ActivityIndicator size="small" color={c.primary} />
                  <Text className="text-xs text-gray-600 dark:text-gray-400">
                    Recalculating…
                  </Text>
                </View>
              ) : fork.isStale ? (
                calculationError ? (
                  <Text className="text-sm font-semibold text-red-700 dark:text-red-400">
                    Calculation failed
                  </Text>
                ) : (
                  <Text className="text-xs text-amber-700 dark:text-amber-500">
                    Default plan changed
                  </Text>
                )
              ) : fork.lastResult == null ? (
                <Text className="text-xs text-gray-500">Not calculated yet</Text>
              ) : fork.lastResult.beginYear != null ? (
                <Text className="text-base font-bold text-gray-900 dark:text-gray-100">
                  {describeFi(fork.lastResult.beginYear, fiCtx)}
                </Text>
              ) : (
                <Text className="text-sm font-semibold text-red-700 dark:text-red-400">
                  No path to FI under these assumptions
                </Text>
              )}
              {delta && <Badge variant={delta.variant} label={delta.label} />}
            </View>
          </View>
          <Ionicons name="chevron-forward" size={18} color={c.textTertiary} />
        </Pressable>
        {canRecalculate && !isRecalculating && (
          <Pressable
            onPress={onRecalculate}
            disabled={recalculateDisabled}
            className={
              'mt-3 self-start rounded-lg px-3 py-1.5 border ' +
              (recalculateDisabled
                ? 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800 opacity-60'
                : 'border-forest-200 bg-forest-50 dark:border-forest-800 dark:bg-forest-900/30')
            }
            accessibilityRole="button"
            accessibilityLabel={`Recalculate ${fork.name}`}
          >
            <Text
              className={
                'text-xs font-semibold ' +
                (recalculateDisabled
                  ? 'text-gray-500 dark:text-gray-400'
                  : 'text-forest-700 dark:text-green-500')
              }
            >
              {calculationError ? 'Try again' : 'Recalculate'}
            </Text>
          </Pressable>
        )}
      </Card>
    </View>
  );
}
